import { useCallback, useEffect, useMemo, useState } from 'react'

import useI18n from 'hooks/useI18n'
import {
    ReadCompetitiveIntelligenceAccess,
    ReadCompetitiveIntelligenceOverview,
    TriggerCompetitiveIntelligenceSync,
} from 'services/competitive-intelligence'
import { trackEvent, trackEventOnce } from 'services/analytics'
import { apiRequest, exposeStrapiError } from 'utils'

const LANGUAGE_LOCALES = {
    Chinese: 'zh-CN',
    English: 'en-US',
    German: 'de-DE',
    Hindi: 'hi-IN',
    Japanese: 'ja-JP',
    Portuguese: 'pt-BR',
    Spanish: 'es-ES',
}

function errorStatus(result) {
    return Number(result?.error?.status || 0)
}

export default function useController() {
    const { language, t } = useI18n()
    const [state, setState] = useState({
        status: 'loading',
        overview: null,
        canSync: false,
        syncStatus: 'idle',
    })

    const fail = useCallback((status, reason, result) => {
        if (result?.error && ![401, 403].includes(errorStatus(result))) {
            exposeStrapiError(result)
        }
        trackEvent('competitive_intelligence_load_failed', { reason })
        setState({ status, overview: null, canSync: false, syncStatus: 'idle' })
    }, [])

    const load = useCallback(async () => {
        setState({ status: 'loading', overview: null, canSync: false, syncStatus: 'idle' })
        const accessResult = await apiRequest(ReadCompetitiveIntelligenceAccess)

        if (!accessResult) {
            fail('unavailable', 'access_unavailable', accessResult)
            return
        }
        if (accessResult?.error) {
            const status = errorStatus(accessResult)
            fail([401, 403].includes(status) ? 'denied' : status >= 500 ? 'unavailable' : 'error', `access_${status || 'error'}`, accessResult)
            return
        }
        if (accessResult?.data?.enabled !== true) {
            fail('unavailable', 'feature_disabled', accessResult)
            return
        }
        if (accessResult?.data?.canRead !== true) {
            fail('denied', 'access_denied', accessResult)
            return
        }

        const overviewResult = await apiRequest(ReadCompetitiveIntelligenceOverview)
        if (!overviewResult) {
            fail('unavailable', 'overview_unavailable', overviewResult)
            return
        }
        if (overviewResult?.error) {
            const status = errorStatus(overviewResult)
            fail([401, 403].includes(status) ? 'denied' : status >= 500 ? 'unavailable' : 'error', `overview_${status || 'error'}`, overviewResult)
            return
        }

        const overview = overviewResult?.data || {}
        const empty = !overview.competitors
            && !overview.researchRecords
            && !overview.marketItems
            && !overview.socialPosts
            && !overview.sources?.length

        trackEventOnce('competitive_intelligence_overview_loaded', {
            competitive_section: 'overview',
            source_count: Array.isArray(overview.sources) ? overview.sources.length : 0,
        }, 'overview-loaded')
        setState({
            status: empty ? 'empty' : 'ready',
            overview,
            canSync: accessResult?.data?.canSync === true,
            syncStatus: 'idle',
        })
    }, [fail])

    const sync = useCallback(async () => {
        if (!state.canSync || state.syncStatus === 'syncing') return
        setState((current) => ({ ...current, syncStatus: 'syncing' }))
        const result = await apiRequest(TriggerCompetitiveIntelligenceSync)
        if (!result || result?.error) {
            if (result?.error && ![401, 403, 429].includes(errorStatus(result))) {
                exposeStrapiError(result)
            }
            trackEvent('competitive_intelligence_sync_failed', {
                status: errorStatus(result) || 0,
            })
            setState((current) => ({ ...current, syncStatus: 'error' }))
            return
        }
        trackEvent('competitive_intelligence_sync_started', {
            pipeline_id: result?.data?.pipelineId || null,
        })
        setState((current) => ({ ...current, syncStatus: 'started' }))
    }, [state.canSync, state.syncStatus])

    useEffect(() => {
        trackEventOnce('competitive_intelligence_opened', { competitive_section: 'overview' }, 'hub-opened')
        load()
    }, [load])

    const locale = LANGUAGE_LOCALES[language] || 'en-US'
    const formatCount = useMemo(() => new Intl.NumberFormat(locale).format, [locale])
    const formatDate = useCallback((value) => {
        if (!value) return t('competitive_intelligence_freshness_unknown')
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return t('competitive_intelligence_freshness_unknown')
        return new Intl.DateTimeFormat(locale, {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(date)
    }, [locale, t])

    return {
        ...state,
        formatCount,
        formatDate,
        retry: load,
        sync,
        t,
    }
}
